import React, { useEffect, useState } from 'react';
import { ActivityIndicator, Image, FlatList } from "react-native"
import type { MyPlayerProps, PlayerPodcastEpisodeProps, PodcastEpisodesProps, PodcastProps, PublicPlayerProps } from '../../types';
import { PodcastApi } from './api';
import { SocialPodcastHelpers } from '../api';
import { Icons } from '../../Components';
import PodcastEpisodeCard from '../PodcastEpisodeCard';
import moment from 'moment-mini';
import PlayerCard from '../PlayerCard';
import ClaimPodcastModal from './components/ClaimPodcastModal';
import { Button, Text, View } from '../../Components/Themed';
import { useColors } from '../../constants/useColors';
import Pagination from '../../Components/Pagination';

type PodcastModuleProps = {
    player_id?:string,
    podcast_id:string,
    padding_insets?:{ top:number, bottom:number },
    onBack?:() => void,
    onShareEpisode?:(pd:PodcastEpisodesProps) => void,
    onSharePodcast?:(p:PodcastProps) => void,
    onSelectEpisode:(pe:PodcastEpisodesProps) => void
    onSelectPlayer?:(p:PublicPlayerProps) => void
}
const sections = ['header', 'players', 'episodes'];
const PodcastModule = ({ podcast_id, player_id, padding_insets, onSelectEpisode, onBack, onShareEpisode, onSharePodcast, onSelectPlayer }:PodcastModuleProps) => {
    const Colors = useColors();
    const [ module_size, setModuleSize ] = useState({ height:700, width:330 });
    const [ show_claim, setShowClaim ] = useState(false);
    const [ pod_data, setPodData ] = useState<{
        loading:boolean,
        player?:MyPlayerProps,
        episodes_loading:boolean,
        podcast?:PodcastProps,
        podcast_episodes:PodcastEpisodesProps[],
        player_podcast_episodes:PlayerPodcastEpisodeProps[],
        players:PublicPlayerProps[],
        episodes_offset:number
    }>({
        loading:false,
        episodes_loading: false,
        player_podcast_episodes:[],
        podcast_episodes:[],
        players:[],
        episodes_offset:0
    });
    const { loading, episodes_loading, podcast, podcast_episodes, episodes_offset, players, player_podcast_episodes, player } = pod_data;
    const claimed_player = pod_data.players.find(p => p.player_id == podcast?.claimed_player_id);
    useEffect(() => {
        PodcastApi.setEnvironment();
        getPodDataFromServer(podcast_id, 0);
    },[podcast_id]);

    const getPodDataFromServer = async(id:string, offset:number) => {
        setPodData({ ...pod_data, loading:true });
        const pod = await PodcastApi.getPodcastById(id);
        const pod_ep = await PodcastApi.getPodcastEpisodesByPodcast(id, offset);
        let me:MyPlayerProps | undefined = undefined;
        if(player_id){
            me = await PodcastApi.getMyDetails();
        }
        let player_ids = pod?.player_ids ?? []
        if(pod?.claimed_player_id){ player_ids.push(pod.claimed_player_id) }
        const ps = await PodcastApi.getPlayersByPlayerIds(pod?.player_ids);
        setPodData({
            ...pod_data,
            loading: false,
            episodes_loading: false,
            podcast: pod,
            player: me,
            players:ps,
            podcast_episodes: pod_ep.podcast_episodes,
            player_podcast_episodes: pod_ep.player_podcast_episodes,
            episodes_offset:offset
        })
    }

    const getEpisdoesFromServer = async(id:string, offset:number) => {
        setPodData({ ...pod_data, episodes_loading: true });
        const pod_eps = await PodcastApi.getPodcastEpisodesByPodcast(id, offset);
        setPodData({
            ...pod_data,
            episodes_loading: false,
            podcast_episodes: pod_eps.podcast_episodes,
            player_podcast_episodes: pod_eps.player_podcast_episodes,
            episodes_offset: offset
        })
    }

    const approveClaim = async() => {
        if(!player || player.role != 'admin'){ return alert('Not eligible to approve this request') }
        const pod = await PodcastApi.approveClaim(podcast_id);
        setPodData({
            ...pod_data,
            podcast: pod
        })
    }

    const renderPlayers = (data:{item:PublicPlayerProps, index:number}) => {
        return (
            <View float style={{ padding:4 }}>
                <PlayerCard
                    player={data.item}
                    width={135}
                    show_follow
                    alignment='vertical'
                    onSelectPlayer={(p) => onSelectPlayer ? onSelectPlayer(p) : console.log('')}
                    onFollowingUpdate={(pf) => console.log(pf)}
                />
            </View>
        )
    }


    const renderEpsidoes = (data: { item:PodcastEpisodesProps, index:number }) => {
        const ep_width = (module_size.width - 60) / 2
        const ppe = player_podcast_episodes.find(pp => pp.podcast_episode_id == data.item.podcast_episode_id);
        return (
            <View key={data.item.podcast_episode_id.toString()} float style={{ margin:7 }}>
                <PodcastEpisodeCard 
                    podcast_episode={data.item}
                    player_podcast_episode={ppe}
                    width={ep_width}
                    onShare={onShareEpisode}
                    onSelectEpisode={onSelectEpisode}
                />
            </View>
        )
    }


    let height = module_size.height * 0.5
    let width = module_size.width

    const renderSections = (data:{ item:string, index:number }) => {
        switch(data.item){
            case 'header':
                if(!podcast){ return <></> }
                let description = `${podcast.description.slice(0,200)}...`
                if(podcast.description_override){ description = `${podcast.description.slice(0,200)}...` }
                
                return (
                    <View nativeID='podcast_header'>
                        <Image
                            source={{ uri: SocialPodcastHelpers.getPodcastImage(podcast) }}
                            style={{ width, height }}
                            resizeMode='cover'
                        />
                        <View type='blur' style={{ padding:20,  position:'absolute', bottom:0, left:0, right:0 }}>
                            <Button 
                                type='success'
                                onPress={() => {
                                    let episode = podcast_episodes.sort((a,b) => moment(b.publish_date).unix() - moment(a.publish_date).unix())[0]
                                    if(!episode){ return alert('Unable to process') }
                                    onSelectEpisode(episode)
                                }}
                                disabled={podcast_episodes.length == 0 ? true : false}
                                style={{ padding:10, borderRadius:8, width:module_size.width*0.7, flexDirection:'row', alignItems:'center', justifyContent:'center', alignSelf:'center' }}>
                                <Icons.PlayIcon size={20} color={Colors.text.action}/>
                                <Text style={{ marginLeft:15 }} weight='bold' color={Colors.text.h1}>Play Episode</Text>
                            </Button>
                            {!podcast.claim_status ?
                            <Button 
                                type='success'
                                style={{ padding:10, marginTop:5, borderRadius:8, width:module_size.width*0.7, flexDirection:'row', alignItems:'center', justifyContent:'center', alignSelf:'center' }}
                                onPress={() => setShowClaim(true)}>
                                <Icons.HeadphoneIcon size={20} color={Colors.text.h1}/>
                                <Text style={{ marginLeft:15 }} weight='bold' color={Colors.text.h1}>Claim This Podcast!</Text>
                            </Button>
                            :podcast.claim_status == 'requested' && player?.role != 'admin' ?
                            <View 
                                style={{ padding:10, marginTop:5, borderRadius:8, width:module_size.width*0.7, flexDirection:'row', alignItems:'center', justifyContent:'center', alignSelf:'center' }}>
                                <Icons.HeadphoneIcon size={20} color={Colors.text.h1}/>
                                <Text style={{ marginLeft:15 }} weight='bold' color={Colors.text.h1}>Claim Requested!</Text>
                            </View>
                            :podcast.claim_status == 'requested' && player?.role == 'admin' ?
                            <Button 
                                type='success'
                                style={{ padding:10, marginTop:5, borderRadius:8, width:module_size.width*0.7, flexDirection:'row', alignItems:'center', justifyContent:'center', alignSelf:'center' }}
                                onPress={() => approveClaim()}>
                                <Icons.HeadphoneIcon size={20} color={Colors.text.h1}/>
                                <Text style={{ marginLeft:15 }} weight='bold' color={Colors.text.h1}>Approve Claim Request!</Text>
                            </Button>
                            :<></>}
                            <View transparent style={{ marginTop:10 }}>
                                <Text size={24} weight='bold' color={Colors.text.h1}>{podcast.title}</Text>
                                <Text style={{ marginTop:4 }} color={Colors.text.h1} weight='semibold'>{description}</Text>
                            </View>
                            {podcast.claim_status == 'claimed' ?
                            <View transparent style={{ position:'absolute', top:20, left:5, height:40, width:40, borderRadius:100, justifyContent:'center', alignItems:'center' }}>
                                <Icons.CheckCirlceIcon size={30} color={Colors.text.success} />
                            </View>
                            :<></>}
                            {podcast.claim_status == 'claimed' && claimed_player ?
                            <Button transparent float style={{ position:'absolute', padding:0, top:20, right:5, borderRadius:100, justifyContent:'center', alignItems:'center' }}
                            onPress={() => onSelectPlayer ? onSelectPlayer(claimed_player) : console.log('')}>
                                  <Image
                                    source={{ uri: claimed_player.profile_pic && claimed_player.profile_pic != '' ? claimed_player.profile_pic:'https://res.cloudinary.com/hoabts6mc/image/upload/v1722453927/default_man_n96ofq.webp' }}
                                    style={{ height:40, width:40, borderRadius:100 }}
                                    resizeMode='cover'
                                />
                            </Button>
                            :<></>}
                        </View>
                        <View transparent style={{ position:'absolute', top:padding_insets?.top ?? 0, left:0, right:0, flexDirection:'row', alignItems:'center', padding:10 }}>
                            {onBack ?
                            <Button 
                                float
                                style={{  height:50, width:50, borderRadius:100, justifyContent:'center', alignItems:'center' }}
                                onPress={() => onBack()}>
                                <Icons.ChevronIcon direction='left' color={Colors.text.action} size={14} />
                            </Button> 
                            :<></>}
                            <View style={{ flex:1 }} />
                            {onSharePodcast ?
                            <Button 
                                float
                                style={{ height:50, width:50, borderRadius:100, justifyContent:'center', alignItems:'center' }}
                                onPress={() => onSharePodcast(podcast)}>
                                <Icons.ShareIcon color={Colors.text.action} size={14} />
                            </Button> 
                            :<></>}
                        </View>
                    </View>
                )
            case 'players':
                if(players.length == 0){ return <></> }
                return (
                    <View>
                        <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                            <View transparent style={{ flex:1 }}>
                                <Text theme='h1'>Podcasters</Text>
                                <Text style={{ marginTop:3 }} theme='description'>Below are the players that put this podcast on! Check out their profiles</Text>
                            </View>
                        </View>
                        <View style={{ padding:10 }}>
                            <FlatList
                                key='podcastors'
                                data={players}
                                horizontal
                                showsHorizontalScrollIndicator={false}
                                renderItem={renderPlayers}
                                keyExtractor={(item) => item.player_id.toString()}
                            />
                        </View>
                    </View>
                )
            case 'episodes':
                return (
                    <View>
                        <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                            <View transparent style={{ flex:1 }}>
                                <Text theme='h1'>Episodes</Text>
                                <Text style={{ marginTop:3 }} theme='description'>Select an episode below to listed to it!</Text>
                            </View>
                        </View>
                        <View transparent>
                            <Pagination
                                offset={episodes_offset}
                                onNext={() => getEpisdoesFromServer(podcast_id, episodes_offset + 1)}
                                onPrevious={() => getEpisdoesFromServer(podcast_id, episodes_offset - 1)}
                            />
                            {episodes_loading ?
                            <ActivityIndicator style={{ padding:20, alignSelf:'center' }} size='large' color={Colors.text.h1} />
                            :<></>}
                            <View transparent style={{ flexDirection:'row', flexWrap: 'wrap', justifyContent:'center' }}>
                                {podcast_episodes.sort((a,b) => moment(b.publish_date).unix() - moment(a.publish_date).unix()).map((e,i) => {
                                    return renderEpsidoes({ item:e, index:i })
                                })}
                            </View>
                        </View>
                    </View>
                )
            default: return <></>
        }
    }


    return (
        <View style={{ flex:1 }}>
            <View style={{ flex:1 }} onLayout={(ev) => {
            const { height, width } = ev.nativeEvent.layout
            setModuleSize({ width, height });
        }}>
                <FlatList 
                    data={sections}
                    refreshing={pod_data.loading}
                    onRefresh={() => getPodDataFromServer(podcast_id, 0)}
                    key={'podcast_sections'}
                    keyExtractor={(item) => item}
                    renderItem={renderSections}
                />
                {loading ?
                <View type='blur' style={{ position:'absolute', top:0, left:0, right:0, bottom:0 }}>
                    <ActivityIndicator style={{ padding:20, alignSelf:'center' }} size='large' color={Colors.text.h1} />
                </View>
                :<></>}
            </View>
            {show_claim && podcast ?
            <View type='blur' style={{ position:'absolute', top:0, right:0, left:0, bottom:0, justifyContent:'center', alignItems:'center', padding:20 }}>
                <View style={{ maxWidth: 500 }}>
                    <ClaimPodcastModal
                        podcast={podcast}
                        width={module_size.width - 20}
                        onClose={() => setShowClaim(false)}
                        onPodcastUpdate={(pod) => {
                            setShowClaim(false);
                            setPodData({
                                ...pod_data,
                                podcast: pod
                            })
                        }}
                    />
                </View>
                </View>
            :<></>}
            
        </View>
    )
}

export default PodcastModule

